import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, ArrowRight, Sparkles, PenTool, Type, Hash, Baseline, TrendingUp } from 'lucide-react';

// ─── Tool metadata for cross-linking ────────────────────────────
const ALL_TOOLS = [
  { id: 'cursive', name: 'Cursive Handwriting Worksheets', path: '/tools/cursive', description: 'Create beautiful cursive handwriting worksheets with elegant flowing fonts for learning connected letter writing.', icon: PenTool, color: 'text-bastion-black', bg: 'bg-bastion-gray-100' },
  { id: 'print', name: 'Print Handwriting Worksheets', path: '/tools/print', description: 'Create professional print handwriting practice sheets with structured lines and quality fonts.', icon: Type, color: 'text-bastion-black', bg: 'bg-bastion-gray-100' },
  { id: 'name-tracing', name: 'Name Tracing Worksheets', path: '/tools/name-tracing', description: 'Generate personalized name tracing worksheets with multiple font options for custom practice.', icon: Sparkles, color: 'text-bastion-black', bg: 'bg-bastion-gray-100' },
  { id: 'tracing-lines', name: 'Tracing Lines Worksheets', path: '/tools/tracing-lines', description: 'Practice pre-writing skills with guided tracing lines for essential pen control development.', icon: TrendingUp, color: 'text-bastion-black', bg: 'bg-bastion-gray-100' },
  { id: 'alphabet-tracing', name: 'Alphabet Tracing Worksheets', path: '/tools/alphabet-tracing', description: 'Build strong letter formation skills with customizable alphabet tracing worksheets.', icon: Baseline, color: 'text-bastion-black', bg: 'bg-bastion-gray-100' },
  { id: 'number-tracing', name: 'Number Tracing Worksheets', path: '/tools/number-tracing', description: 'Master number writing with guided practice pages featuring numbers 0-9.', icon: Hash, color: 'text-bastion-black', bg: 'bg-bastion-gray-100' },
];

// ─── Tool-specific content ──────────────────────────────────────
const TOOL_CONTENT = {
  cursive: {
    masterTitle: 'Everything You Need to Master Cursive',
    features: [
      { title: 'Sentence & Paragraph Practice', text: 'Go beyond simple words. Paste poems, quotes, or stories to create custom cursive writing worksheets. Perfect for practicing flow and writing speed with longer texts.' },
      { title: 'Cursive for Adults & Professionals', text: "It's never too late to improve. Generate beautiful cursive practice sheets for adults to refine your handwriting, fix messy writing, or practice inspirational quotes." },
      { title: 'Alphabet & Letter Charts', text: 'Master both uppercase and lowercase letters with guided tracing lines. Practice the complete A-Z alphabet with our dedicated alphabet tool.' },
    ],
    enhanceTitle: 'Enhance Your Handwriting Skills',
    enhanceSubtitle: 'Cursive practice builds flow and rhythm. Learn how to develop smooth letter connections and elegant handwriting.',
    guideTitle: 'Your Complete Guide to Mastering Cursive',
    guideSubtitle: 'Master the art of beautiful handwriting with our complete tools and expert guidance.',
    steps: [
      { num: 1, title: 'Enter Your Text', desc: 'Type any letters, words, or sentences you wish to practice in the text field.' },
      { num: 2, title: 'Customize Your Style', desc: 'Choose from our collection of educational fonts and adjust settings to fit your needs.' },
      { num: 3, title: 'Download Instantly', desc: "Click 'Generate PDF' to get your high-quality, printable practice sheet immediately." },
    ],
    tips: [
      { bold: 'Start with Basic Strokes:', text: 'Before writing full letters, practice basic strokes like loops and curves to build muscle memory.' },
      { bold: 'Focus on Letter Connections:', text: 'Watch how letters connect. Make smooth connections between letters to create beautiful flowing text.' },
      { bold: 'Maintain a Consistent Slant:', text: 'A uniform slant angle gives your writing a professional and tidy appearance.' },
      { bold: 'Practice Regularly:', text: 'Consistent, short practice sessions (10-15 minutes daily) are more effective than infrequent, long ones.' },
    ],
    benefitsTitle: 'The Ultimate Tool for Cursive Penmanship',
    benefitsSubtitle: 'Discover why educators and parents choose our worksheet generator for superior results.',
    benefits: [
      'Professional quality with excellent free options',
      'High-quality educational cursive fonts',
      'Instant PDF generation and download',
      'Suitable for all ages and skill levels',
      'Customizable worksheets for personalized learning',
      'Educational standards compliant',
    ],
    faq: [
      { q: 'How do I use the worksheet generator?', a: 'Simply enter your desired text in the generator above, customize your settings, and download the PDF. Print it at home and start practicing! The worksheets include dotted lines to guide your writing practice.' },
      { q: 'Do I need to register to use the worksheet generator?', a: 'No registration is required for basic worksheet generation. Simply enter your text, customize your settings, and download your free PDF worksheet instantly.' },
      { q: 'What is the best way for a beginner to learn cursive?', a: 'Start by practicing single letters. Focus on how each letter looks and how it connects to the next one. Then practice letter pairs, then words, then sentences. Practice for 10-15 minutes every day for best results.' },
      { q: 'Can this tool create an alphabet worksheet?', a: 'Yes, simply type "Aa Bb Cc..." into the text field to create a complete alphabet tracing worksheet, which is perfect for mastering letter formation.' },
      { q: 'Can I create worksheets for full paragraphs?', a: 'Yes! Our generator handles multiple lines of text, making it perfect for practicing full sentences, paragraphs, or copywork exercises.' },
      { q: 'Is this suitable for adults?', a: 'Absolutely. We offer customizable font sizes and guideline options, making these handwriting practice sheets perfect for adults refining professional penmanship.' },
    ],
  },
  print: {
    masterTitle: 'Master Print & Manuscript Writing',
    features: [
      { title: 'Kindergarten & Preschool Basics', text: 'Build a strong foundation with alphabet activities and letter formation exercises. Our tool helps young learners master manuscript writing with guided stroke order and clear layouts.' },
      { title: 'Neat Writing for Adults', text: 'Fix messy penmanship and improve legibility for professional forms and notes. Create customized practice pages for adults to retrain your muscle memory for clear, consistent strokes.' },
      { title: 'Custom Vocabulary Practice', text: 'Create worksheets with specific words or sentences. Perfect for spelling practice, vocabulary building, or any custom content you need to practice.' },
    ],
    enhanceTitle: 'Enhance Your Handwriting Skills',
    enhanceSubtitle: 'Improve clarity and consistency with structured print practice, proper grip, and posture tips.',
    guideTitle: 'Your Guide to Clear and Legible Print Writing',
    guideSubtitle: 'Develop professional manuscript handwriting with our complete tools and expert guidance.',
    steps: [
      { num: 1, title: 'Enter Your Text', desc: 'Type any letters, words, or sentences you wish to practice in the text field.' },
      { num: 2, title: 'Customize Your Style', desc: 'Choose your preferred font, line spacing, and paper size to match your needs.' },
      { num: 3, title: 'Download Instantly', desc: "Click 'Generate PDF' to get your high-quality, printable practice sheet immediately." },
    ],
    tips: [
      { bold: 'Focus on Letter Consistency:', text: 'Make sure each letter is the same size and sits evenly on the baseline for a clean, professional look.' },
      { bold: 'Practice Proper Spacing:', text: 'Keep consistent spacing between letters and words. This is key to improving readability.' },
      { bold: 'Start with Individual Letters:', text: 'Master each letter form before moving to words and sentences for better muscle memory.' },
      { bold: 'Use Guided Lines:', text: 'Practice with lined worksheets to maintain uniform letter height and alignment.' },
    ],
    benefitsTitle: 'Key Benefits of Daily Practice',
    benefitsSubtitle: 'Discover how regular print handwriting practice transforms your writing skills.',
    benefits: [
      'Improved reading comprehension through better visual clarity',
      'Enhanced academic and professional communication',
      'Develops focus and attention to detail',
      'Builds lasting good penmanship habits',
      'Increases confidence in written expression',
      'Professional quality with excellent free options',
    ],
    faq: [
      { q: 'How can these resources improve poor penmanship?', a: 'Our worksheets provide structured practice with guided lines and tracing exercises. Regular practice builds muscle memory, leading to consistent letter formation and improved legibility over time.' },
      { q: 'What is the difference between print and cursive styles?', a: 'Print (manuscript) writing uses separate, unconnected letters, while cursive uses flowing, connected letters. Print is typically taught first as it closely resembles typed text.' },
      { q: 'How do I customize a printable handwriting worksheet?', a: 'Enter your desired text, select a font, adjust line spacing and paper size, then download your custom PDF. All settings can be changed in real-time with instant preview.' },
      { q: 'Is this tool suitable for kindergarten students?', a: 'Yes! Our generator creates age-appropriate worksheets with clear letter forms and generous line spacing, perfect for young learners just starting to write.' },
      { q: 'Can I create full alphabet charts?', a: 'Yes, simply type all the letters of the alphabet into the text field to generate a complete letter practice chart.' },
      { q: 'What is the difference between Print and Cursive?', a: 'Print writing uses individual, separated letters similar to what you see in books. Cursive connects letters together in a flowing style. Both are important skills.' },
    ],
  },
  'name-tracing': {
    masterTitle: 'Everything You Need for Name Practice',
    features: [
      { title: 'Personalized Name Sheets', text: "Create custom worksheets with your child's name. Multiple fonts and styles let you generate exactly the practice sheets they need to master writing their own name." },
      { title: 'Multiple Font Styles', text: 'Choose from cursive, print, and educational fonts to match what your child is learning in school. Each font includes proper letter formation guides.' },
      { title: 'Perfect for Preschool & Kindergarten', text: 'Help young learners build confidence by practicing their own name. Guided tracing lines make it easy for small hands to follow along.' },
    ],
    enhanceTitle: 'Enhance Your Handwriting Skills',
    enhanceSubtitle: 'Name tracing builds letter recognition and fine motor skills. Start with familiar words for faster learning.',
    guideTitle: 'Your Guide to Teaching Name Writing',
    guideSubtitle: 'Help children master writing their name with our tools and expert guidance.',
    steps: [
      { num: 1, title: 'Enter the Name', desc: "Type the name you want to practice. You can use any name - child's name, family names, or vocabulary words." },
      { num: 2, title: 'Choose Your Font', desc: 'Select from educational fonts designed for letter formation practice.' },
      { num: 3, title: 'Download & Practice', desc: "Click 'Generate PDF' to get your personalized name tracing worksheet instantly." },
    ],
    tips: [
      { bold: 'Start with First Name Only:', text: 'Begin with just the first name before adding middle or last names to avoid overwhelming young learners.' },
      { bold: 'Use Uppercase First:', text: 'Start with uppercase letters as they use simpler strokes, then progress to lowercase once confident.' },
      { bold: 'Trace Then Copy:', text: 'Have children trace the dotted letters first, then try writing on blank lines below for independent practice.' },
      { bold: 'Make It Fun:', text: 'Celebrate each practice session. Use stickers or stamps to reward effort and build positive associations with writing.' },
    ],
    benefitsTitle: 'Why Name Tracing Matters',
    benefitsSubtitle: 'Discover the educational benefits of personalized name practice.',
    benefits: [
      'Builds personal connection to writing practice',
      'Develops fine motor skills and hand-eye coordination',
      'Improves letter recognition and formation',
      'Boosts confidence for school readiness',
      'Multiple font options for different learning stages',
      'Instant PDF generation for immediate practice',
    ],
    faq: [
      { q: 'What age is name tracing appropriate for?', a: 'Name tracing is ideal for children ages 3-6, but can benefit anyone learning to write. Preschoolers and kindergarteners especially benefit from this type of personalized practice.' },
      { q: 'Can I use different fonts for the same name?', a: 'Yes! You can generate multiple worksheets with different fonts to practice the same name in various handwriting styles.' },
      { q: 'How many pages does a name tracing worksheet have?', a: 'You can customize the number of pages. Each page contains multiple practice lines with the name for thorough practice.' },
      { q: 'Can I create worksheets for multiple names?', a: 'Yes, you can generate separate worksheets for different names. This is perfect for classrooms or families with multiple children.' },
      { q: 'Is this suitable for children with learning differences?', a: 'Yes, our worksheets are designed with clear, large letter forms and guided lines that work well for children with various learning needs.' },
    ],
  },
  'tracing-lines': {
    masterTitle: 'Build Essential Pre-Writing Skills',
    features: [
      { title: 'Straight & Curved Lines', text: 'Start with basic horizontal and vertical lines, then progress to curves, zigzags, and spirals. Each pattern builds specific muscle control needed for letter writing.' },
      { title: 'Pre-Writing for Toddlers & Preschool', text: 'Designed for the youngest learners. Large, clear line patterns help develop the hand-eye coordination needed before formal letter writing begins.' },
      { title: 'Fine Motor Skill Development', text: 'Tracing activities strengthen the small muscles in hands and fingers. This foundational skill is essential for pencil grip and control in all handwriting.' },
    ],
    enhanceTitle: 'Enhance Your Handwriting Skills',
    enhanceSubtitle: 'Line tracing develops pen control and fine motor skills. Build the foundation for beautiful handwriting.',
    guideTitle: 'Your Guide to Pre-Writing Practice',
    guideSubtitle: 'Develop essential pen control skills with our line tracing tools and expert guidance.',
    steps: [
      { num: 1, title: 'Choose Line Type', desc: 'Select from straight, curved, zigzag, wave, spiral, and other line patterns.' },
      { num: 2, title: 'Customize Settings', desc: 'Adjust line thickness, color, spacing, and paper size to match your needs.' },
      { num: 3, title: 'Download & Practice', desc: "Click 'Generate PDF' to get your tracing worksheet instantly." },
    ],
    tips: [
      { bold: 'Start Simple:', text: 'Begin with straight horizontal lines before progressing to vertical, diagonal, and curved patterns.' },
      { bold: 'Use Proper Grip:', text: 'Encourage a tripod grip (thumb, index, and middle finger) from the start for best results.' },
      { bold: 'Follow the Direction:', text: 'Practice left-to-right and top-to-bottom movements to build habits that transfer to letter writing.' },
      { bold: 'Progress Gradually:', text: 'Move from thick lines to thinner ones as control improves, then transition to letter formation practice.' },
    ],
    benefitsTitle: 'Why Pre-Writing Skills Matter',
    benefitsSubtitle: 'Understanding the importance of line tracing in handwriting development.',
    benefits: [
      'Builds hand-eye coordination essential for writing',
      'Strengthens fine motor muscles in fingers and hands',
      'Develops pencil grip and pressure control',
      'Prepares children for formal letter writing',
      'Multiple line patterns for progressive difficulty',
      'Perfect for toddlers, preschoolers, and special needs',
    ],
    faq: [
      { q: 'What age should children start tracing lines?', a: 'Children can start simple line tracing activities as early as age 2-3. Begin with thick, simple patterns and progress to more complex ones as their motor skills develop.' },
      { q: 'What line types are available?', a: 'We offer straight (horizontal, vertical, diagonal), curved, zigzag, wave, loop, spiral, and combination patterns to build various motor skills.' },
      { q: 'How does line tracing help with handwriting?', a: 'Line tracing develops the foundational motor skills needed for letter formation: pen control, hand-eye coordination, and proper stroke direction.' },
      { q: 'Can adults benefit from line tracing?', a: 'Yes! Adults recovering from hand injuries or those wanting to improve pen control can benefit from line tracing exercises as a warmup before handwriting practice.' },
      { q: 'How often should children practice tracing?', a: 'Short, daily sessions of 5-10 minutes are most effective for young children. Consistency is more important than duration.' },
    ],
  },
  'alphabet-tracing': {
    masterTitle: 'Master the Complete Alphabet',
    features: [
      { title: 'Uppercase & Lowercase Practice', text: 'Practice both uppercase and lowercase letter forms with guided tracing lines. Each letter includes proper stroke order for correct formation.' },
      { title: 'A to Z Coverage', text: 'Complete alphabet worksheets covering all 26 letters. Each page focuses on one letter for thorough practice and mastery.' },
      { title: 'Educational Font Options', text: 'Choose from fonts designed specifically for letter learning. Each font shows clear, recognizable letter forms perfect for educational settings.' },
    ],
    enhanceTitle: 'Enhance Your Handwriting Skills',
    enhanceSubtitle: 'Alphabet tracing builds letter recognition and proper formation. Master each letter for confident writing.',
    guideTitle: 'Your Guide to Alphabet Mastery',
    guideSubtitle: 'Help learners master every letter of the alphabet with our tools and expert guidance.',
    steps: [
      { num: 1, title: 'Choose Your Font', desc: 'Select from educational fonts designed for clear letter formation and easy learning.' },
      { num: 2, title: 'Customize Settings', desc: 'Adjust line color, text style, paper size, and other options to fit your needs.' },
      { num: 3, title: 'Download All 26 Pages', desc: "Click 'Generate PDF' to get your complete A-Z alphabet tracing workbook instantly." },
    ],
    tips: [
      { bold: 'Learn in Groups:', text: 'Group similar-shaped letters together (like c, o, a, d, g) to build on common stroke patterns.' },
      { bold: 'Practice Stroke Order:', text: 'Always start letters from the correct starting point. This builds good habits that improve writing speed later.' },
      { bold: 'Uppercase Then Lowercase:', text: 'Master uppercase letters first as they use simpler, larger strokes, then progress to lowercase.' },
      { bold: 'Review Regularly:', text: 'Return to previously learned letters often to reinforce muscle memory and prevent regression.' },
    ],
    benefitsTitle: 'Benefits of Alphabet Practice',
    benefitsSubtitle: 'Why systematic alphabet tracing is essential for writing development.',
    benefits: [
      'Complete A-Z coverage in one worksheet set',
      'Builds proper letter formation from the start',
      'Develops letter recognition alongside writing',
      'Suitable for preschool through adult learners',
      'Educational fonts designed for clarity',
      'Free instant PDF download with 26 pages',
    ],
    faq: [
      { q: 'How many pages does the alphabet worksheet generate?', a: 'The alphabet worksheet generates 26 pages, one for each letter of the alphabet (A through Z), with both uppercase and lowercase practice.' },
      { q: 'What age is alphabet tracing recommended for?', a: 'Alphabet tracing is ideal for children ages 4-7, but is useful for anyone learning letter formation, including ESL learners and adults improving their handwriting.' },
      { q: 'Can I choose specific letters to practice?', a: 'The alphabet tracing tool generates all 26 letters. For specific letter practice, try our cursive or print handwriting tools where you can type specific letters.' },
      { q: 'Are both uppercase and lowercase letters included?', a: 'Yes! Each page includes both uppercase and lowercase versions of the letter for comprehensive practice.' },
      { q: 'What fonts are available for alphabet tracing?', a: 'We offer several educational fonts designed for letter learning, including print-style and cursive-style options to match what your school teaches.' },
    ],
  },
  'number-tracing': {
    masterTitle: 'Master Number Writing Skills',
    features: [
      { title: 'Numbers 0-9 Practice', text: 'Complete number practice covering all digits from 0 to 9. Each page focuses on one number with multiple tracing lines for thorough practice.' },
      { title: 'Math Readiness Foundation', text: 'Number writing is a foundational math skill. Clear number formation helps children with arithmetic, telling time, and understanding quantities.' },
      { title: 'Step-by-Step Learning', text: 'Guided tracing lines show proper stroke order for each number. Progressive difficulty levels help learners build confidence gradually.' },
    ],
    enhanceTitle: 'Enhance Your Handwriting Skills',
    enhanceSubtitle: 'Number tracing builds essential math readiness skills. Practice proper number formation for academic success.',
    guideTitle: 'Your Guide to Number Writing Mastery',
    guideSubtitle: 'Help learners master number writing with our tools and expert guidance.',
    steps: [
      { num: 1, title: 'Choose Your Font', desc: 'Select from educational fonts designed for clear number formation.' },
      { num: 2, title: 'Customize Settings', desc: 'Adjust line color, text style, paper size, and spacing options.' },
      { num: 3, title: 'Download All 10 Pages', desc: "Click 'Generate PDF' to get your complete 0-9 number tracing workbook instantly." },
    ],
    tips: [
      { bold: 'Start with Simple Numbers:', text: 'Begin with numbers that use simple strokes (1, 7, 4) before progressing to curved numbers (0, 6, 8, 9).' },
      { bold: 'Practice Correct Starting Points:', text: 'Each number has a specific starting point. Learning the correct stroke order early prevents bad habits.' },
      { bold: 'Connect Numbers to Counting:', text: 'Pair writing practice with counting objects to build number sense alongside formation skills.' },
      { bold: 'Use Consistent Sizing:', text: 'Practice making each number the same height and width for neat, legible number writing.' },
    ],
    benefitsTitle: 'Benefits of Number Tracing Practice',
    benefitsSubtitle: 'Why systematic number practice is essential for early math development.',
    benefits: [
      'Complete 0-9 coverage in one worksheet set',
      'Builds proper number formation habits',
      'Supports early math learning and readiness',
      'Develops fine motor skills specific to numerals',
      'Educational fonts designed for clarity',
      'Free instant PDF download with 10 pages',
    ],
    faq: [
      { q: 'How many pages does the number tracing worksheet generate?', a: 'The number tracing worksheet generates 10 pages, one for each digit (0 through 9), with multiple practice lines per number.' },
      { q: 'What age is number tracing recommended for?', a: 'Number tracing is ideal for children ages 3-6, but is useful for anyone learning proper number formation, including children with learning differences.' },
      { q: 'Can I practice specific numbers?', a: 'The number tracing tool generates all 10 digits (0-9). Each page focuses on one number for thorough practice.' },
      { q: 'How does number tracing support math learning?', a: 'Writing numbers helps children internalize their shapes and values. This physical connection supports number recognition, counting, and basic arithmetic skills.' },
      { q: 'Are the numbers suitable for international standards?', a: 'Yes, our number fonts use universally recognized digit forms that are standard across educational systems worldwide.' },
    ],
  },
};

// ─── FAQ Accordion Item ─────────────────────────────────────────
function FaqItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-lg">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="font-medium text-gray-900">{question}</span>
        {open ? <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-6 pb-4 text-gray-700 text-sm leading-relaxed">
          {answer}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────
export default function ToolPageSections({ toolType }) {
  const content = TOOL_CONTENT[toolType];
  if (!content) return null;

  const otherTools = ALL_TOOLS.filter(t => t.id !== toolType);
  // Pick 4 tools for the "More Free Tools" section
  const moreTools = otherTools.slice(0, 4);
  // Pick 4 tools for the "Enhance" section (with icons)
  const enhanceTools = otherTools.slice(0, 4);

  return (
    <>
      {/* ── More Free Handwriting Tools ─────────────────────── */}
      <section className="mt-16">
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">More Free Handwriting Tools</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {moreTools.map(tool => (
            <Link
              key={tool.id}
              to={tool.path}
              className="bg-white rounded-xl border border-gray-100 p-5 hover:shadow-md transition-shadow group"
            >
              <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-bastion-red transition-colors">{tool.name}</h3>
              <p className="text-gray-700 text-sm mb-3 line-clamp-3">{tool.description}</p>
              <span className="text-sm font-medium text-bastion-red flex items-center gap-1 group-hover:gap-2 transition-all">
                Explore <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* ── Everything You Need / Master Section ────────────── */}
      <section className="mt-16">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-8">{content.masterTitle}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {content.features.map((feat, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="font-semibold text-gray-900 mb-2">{feat.title}</h3>
              <p className="text-gray-700 text-sm leading-relaxed">{feat.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Enhance Your Handwriting Skills ──────────────────── */}
      <section className="mt-16">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-2">{content.enhanceTitle}</h2>
        <p className="text-gray-700 text-center mb-8 max-w-2xl mx-auto">{content.enhanceSubtitle}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {enhanceTools.map(tool => {
            const Icon = tool.icon;
            return (
              <Link
                key={tool.id}
                to={tool.path}
                className="bg-white rounded-xl border border-gray-100 p-5 hover:shadow-md transition-shadow group"
              >
                <div className={`w-10 h-10 ${tool.bg} rounded-lg flex items-center justify-center mb-3`}>
                  <Icon className={`w-5 h-5 ${tool.color}`} />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{tool.name}</h3>
                <p className="text-gray-700 text-sm mb-3 line-clamp-2">{tool.description}</p>
                <span className={`text-sm font-medium ${tool.color} flex items-center gap-1`}>
                  Create Worksheet <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ── Complete Guide Section ───────────────────────────── */}
      <section className="mt-16 bg-gray-50 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-2">{content.guideTitle}</h2>
          <p className="text-gray-700 text-center mb-10 max-w-2xl mx-auto">{content.guideSubtitle}</p>

          {/* 3 Easy Steps */}
          <h3 className="text-xl font-semibold text-gray-900 text-center mb-2">3 Easy Steps to Create Your Worksheet</h3>
          <p className="text-gray-700 text-center text-sm mb-8">Create your perfect practice worksheet in minutes.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {content.steps.map(step => (
              <div key={step.num} className="bg-white rounded-xl border border-gray-100 p-6 text-center">
                <div className="w-10 h-10 bg-bastion-red text-white rounded-full flex items-center justify-center mx-auto mb-3 font-bold">
                  {step.num}
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">{step.title}</h4>
                <p className="text-gray-700 text-sm">{step.desc}</p>
              </div>
            ))}
          </div>

          {/* Expert Tips + Benefits */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Tips */}
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Expert Tips for Effective Practice</h3>
              <p className="text-gray-700 text-sm mb-4">Follow these professional techniques to improve your writing skills effectively.</p>
              <ul className="space-y-3">
                {content.tips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-bastion-red rounded-full mt-2 flex-shrink-0" />
                    <p className="text-gray-700 text-sm">
                      <strong className="text-gray-900">{tip.bold}</strong> {tip.text}
                    </p>
                  </li>
                ))}
              </ul>
            </div>

            {/* Benefits */}
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{content.benefitsTitle}</h3>
              <p className="text-gray-700 text-sm mb-4">{content.benefitsSubtitle}</p>
              <ul className="space-y-3">
                {content.benefits.map((benefit, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="text-bastion-black flex-shrink-0">&#10003;</span>
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ Section ─────────────────────────────────────── */}
      <section className="mt-16 mb-8">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-8">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto space-y-3">
          {content.faq.map((item, i) => (
            <FaqItem key={i} question={item.q} answer={item.a} />
          ))}
        </div>
      </section>
    </>
  );
}
